import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faPlus,
  faEye,
  faEdit,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { convertToFollowup, getContactList } from "../api/contactService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

interface Contact {
  id: number;
  name: string;
  phone: string;   // ✅ updated
  email?: string;
  company_name?: string;
  city?: string;
  state?: string;
}

const ContactList: React.FC = () => {
  const navigate = useNavigate();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    name: "",
    phone: "",
    email: "",
    source: "",
    vertical: "",
    assigned_to: "",
    status: "",
    from_date: "",
    to_date: "",
    company_name: "",
    city: "",
    state: "",
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const [ordering, setOrdering] = useState("");
  const [pageSize, setPageSize] = useState<number | null>(null);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const [openActionId, setOpenActionId] = useState<number | null>(null);

  /* =====================
     SORTING
  ===================== */
  const getSortIcon = (field: string) => {
    if (ordering === field) return faSortUp;
    if (ordering === `-${field}`) return faSortDown;
    return faSort;
  };

  const toggleOrdering = (field: string) => {
    setPage(1); // ✅ add this
    setOrdering((prev) => {
      if (prev === field) return `-${field}`;
      if (prev === `-${field}`) return "";
      return field;
    });
  };

  /* =====================
     FETCH CONTACTS
  ===================== */
  const fetchContacts = async (pageNo: number, appliedFilters = filters) => {
    if (isInitialLoad) setIsInitialLoad(true);
    else setIsFetching(true);

    try {
      const res = await getContactList(pageNo, {
        name: appliedFilters.name,
        phone: appliedFilters.phone,
        email: appliedFilters.email,
        source: appliedFilters.source,
        vertical: appliedFilters.vertical,
        assigned_to: appliedFilters.assigned_to,
        status: appliedFilters.status,
        from_date: appliedFilters.from_date,
        to_date: appliedFilters.to_date,
        company_name: appliedFilters.company_name,
        city: appliedFilters.city,
        state: appliedFilters.state,
        ordering,
      });

      const results = res.data?.results || [];

      setContacts(results);
      setTotalCount(res.data?.count || 0);

      if (pageSize === null && results.length) {
        setPageSize(results.length);
      }
    } finally {
      setIsFetching(false);
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    fetchContacts(page, debouncedFilters);
  }, [page, ordering, debouncedFilters]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, 500);

    return () => clearTimeout(debounce);
  }, [filters]);

  const totalPages =
    pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenActionId(null);
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);
  const handleConvert = async (id: number) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Convert this contact to followup?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, convert",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      await convertToFollowup(id);

      await Swal.fire({
        icon: "success",
        title: "Converted!",
        text: "Contact converted successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      // 🔥 refresh list
      fetchContacts(page, debouncedFilters);
    }catch (err: any) {
  console.error(err);

  const data = err?.response?.data;

  let messages: string[] = [];

  // ✅ CASE 1: { error: { field: "msg" } }
  if (data?.error && typeof data.error === "object") {
    Object.values(data.error).forEach((msg: any) => {
      if (typeof msg === "string") messages.push(msg);
    });
  }

  // ✅ CASE 2: { field: ["msg"] }
  else if (data && typeof data === "object") {
    Object.values(data).forEach((val: any) => {
      if (Array.isArray(val)) {
        messages.push(...val);
      }
    });
  }

  // ✅ SHOW ALL ERRORS
  if (messages.length) {
    toast.error(messages.join(", "));
    return;
  }

  // fallback
  toast.error("Failed to convert followup");
}
  };
  return (
    <>
      <PageHeader
        title="Contact Directory"
        breadcrumbs={[
          { label: "Dashboard", path: "/dashboard" },
          { label: "Masters" },
          { label: "Contact Directory" },
        ]}
      />

      <div className="container mt-4">
        {/* ADD BUTTON */}
        <div className="d-flex justify-content-end mb-4">
          <button
            className="btn btn-primary"
            onClick={() => navigate("/masters/contact/create")}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Contact
          </button>
        </div>

        <div className="card hr-card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Action</th>

                  <th
                    onClick={() => toggleOrdering("name")}
                    className="sortable"
                  >
                    Name <FontAwesomeIcon icon={getSortIcon("name")} />
                  </th>

                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Company</th>

                  <th
                    onClick={() => toggleOrdering("city")}
                    className="sortable"
                  >
                    City <FontAwesomeIcon icon={getSortIcon("city")} />
                  </th>

                  <th
                    onClick={() => toggleOrdering("state")}
                    className="sortable"
                  >
                    State <FontAwesomeIcon icon={getSortIcon("state")} />
                  </th>
                </tr>

                {/* FILTER ROW */}
                <tr className="table-filter-row">
                  <th></th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search name..."
                      value={filters.name}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, name: e.target.value }))
                      }
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search mobile..."
                      value={filters.phone}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d+$/.test(val)) {
                          setFilters((prev) => ({ ...prev, phone: val }));
                        }
                      }}
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search email..."
                      value={filters.email}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, email: e.target.value }))
                      }
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search company..."
                      value={filters.company_name}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, company_name: e.target.value }))
                      }
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search city..."
                      value={filters.city}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, city: e.target.value }))
                      }
                    />
                  </th>

                  <th>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search state..."
                      value={filters.state}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, state: e.target.value }))
                      }
                    />
                  </th>
                </tr>
              </thead>

              <tbody>
                {isInitialLoad ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      No contacts found
                    </td>
                  </tr>
                ) : (
                  contacts.map((c) => (
                    <tr key={c.id}>
                      <td className="position-relative">
                        <FontAwesomeIcon
                          icon={faEllipsisV}
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation(); // ✅ IMPORTANT
                            setOpenActionId(openActionId === c.id ? null : c.id);
                          }}
                        />

                        {openActionId === c.id && (
                          <div className="action-dropdown"
                            onClick={(e) => e.stopPropagation()} >
                            <span
                              onClick={() =>
                                navigate(`/masters/contact/view/${c.id}`)
                              }
                            >
                              <FontAwesomeIcon icon={faEye} /> View
                            </span>

                            <span
                              onClick={() =>
                                navigate(`/masters/contact/edit/${c.id}`)
                              }
                            >
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </span>
                            <span
                              onClick={() => {
                                handleConvert(c.id);
                                setOpenActionId(null);
                              }}>
                              <FontAwesomeIcon icon={faPlus} /> Convert to Followup
                            </span>
                          </div>
                        )}
                      </td>

                      <td>{c.name}</td>
                      <td>{c.phone}</td>
                      <td>{c.email || <span className="text-muted">—</span>}</td>
                      <td>{c.company_name || "—"}</td>
                      <td>{c.city || "—"}</td>
                      <td>{c.state || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="d-flex gap-1 justify-content-end p-3">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (p) => (
                  <button
                    key={p}
                    className={`btn btn-sm ${p === page ? "btn-primary" : "btn-outline-secondary"
                      }`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactList;